import { create } from 'zustand';
import type { NotificationResponseDto } from '@/lib/models';

/**
 * Client-only UI state for the notifications surface: whether the panel is
 * open, which notification's detail pop-up is showing, and the just-arrived
 * notification surfaced as the pop-in banner. Notifications are held as full
 * objects (not just ids) so the pop-up/banner are independent of which
 * tab/search query produced the row. Server data (list, counts) lives in
 * React Query, not here.
 */
interface NotificationsUiState {
  panelOpen: boolean;
  detail: NotificationResponseDto | null;
  /** Just-arrived notification shown as the pop-in banner (null = hidden). */
  incoming: NotificationResponseDto | null;
  /** Monotonic counter — each increment replays the bell's ring animation. */
  ringNonce: number;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  openDetail: (notification: NotificationResponseDto) => void;
  closeDetail: () => void;
  /** Ring the bell only — no banner, no pop-up (the BELL jump level). */
  ring: () => void;
  showIncoming: (notification: NotificationResponseDto) => void;
  dismissIncoming: () => void;
}

const useNotificationsUiStore = create<NotificationsUiState>((set) => ({
  panelOpen: false,
  detail: null,
  incoming: null,
  ringNonce: 0,
  // Opening the panel makes the banner redundant (the list shows the new row),
  // so both open paths clear it.
  openPanel: () => set({ panelOpen: true, incoming: null }),
  closePanel: () => set({ panelOpen: false }),
  togglePanel: () => set((s) => ({ panelOpen: !s.panelOpen, incoming: null })),
  openDetail: (notification) => set({ detail: notification }),
  closeDetail: () => set({ detail: null }),
  // Bump the ring counter without touching the banner or pop-up (BELL jump level).
  ring: () => set((s) => ({ ringNonce: s.ringNonce + 1 })),
  // Always ring; only surface the banner when the panel isn't already showing
  // the list. A newer arrival replaces the banner in place.
  showIncoming: (notification) => set((s) => ({ ringNonce: s.ringNonce + 1, incoming: s.panelOpen ? null : notification })),
  dismissIncoming: () => set({ incoming: null }),
}));

/** Select whether the notification panel is open. */
export const useNotificationsPanelOpen = () => useNotificationsUiStore((s) => s.panelOpen);
/** Select the action to toggle the notification panel. */
export const useToggleNotificationsPanel = () => useNotificationsUiStore((s) => s.togglePanel);
/** Select the action to close the notification panel. */
export const useCloseNotificationsPanel = () => useNotificationsUiStore((s) => s.closePanel);
/** Select the notification whose detail pop-up is open (or null). */
export const useNotificationDetail = () => useNotificationsUiStore((s) => s.detail);
/** Select the action to open a notification's detail pop-up. */
export const useOpenNotificationDetail = () => useNotificationsUiStore((s) => s.openDetail);
/** Select the action to close the detail pop-up. */
export const useCloseNotificationDetail = () => useNotificationsUiStore((s) => s.closeDetail);
/** Select the just-arrived notification shown as the pop-in banner (or null). */
export const useIncomingNotification = () => useNotificationsUiStore((s) => s.incoming);
/** Select the ring counter driving the bell's ring animation. */
export const useNotificationsRingNonce = () => useNotificationsUiStore((s) => s.ringNonce);
/** Select the action that rings the bell only (BELL jump level — no banner, no pop-up). */
export const useRingBell = () => useNotificationsUiStore((s) => s.ring);
/** Select the action that rings the bell and pops the incoming banner. */
export const useShowIncomingNotification = () => useNotificationsUiStore((s) => s.showIncoming);
/** Select the action to dismiss the incoming banner. */
export const useDismissIncomingNotification = () => useNotificationsUiStore((s) => s.dismissIncoming);

/** Imperative reset — for use outside React (e.g. test setup). */
export const resetNotificationsUiStore = () =>
  useNotificationsUiStore.setState({ panelOpen: false, detail: null, incoming: null, ringNonce: 0 });
